import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Sparkles, ArrowLeft, Send, Loader2, Bot, User, Check,
    Copy, Shield, AlertTriangle, Terminal, Code, HelpCircle,
    Maximize2, Minimize2, X, RefreshCw, Layers, ShieldAlert,
    Clock, ExternalLink, ChevronRight, FileText, Globe, Radio
} from "lucide-react";

const quickPrompts = [
    "What triggered this alert?",
    "Can you check the reputation of the URL and check for signs of fraudulent behavior?",
    "Is the file really malicious, or could it be a false positive?",
    "Who else was hit across the fleet?",
    "Generate a containment and remediation plan"
];

export default function InvestigationAsk() {
    const { caseId = "QueryTel-Enterprise-Infrastructure" } = useParams();
    const navigate = useNavigate();

    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [inputQuery, setInputQuery] = useState("");
    const [asking, setAsking] = useState(false);
    const [copiedJsonIdx, setCopiedJsonIdx] = useState(null);

    const chatEndRef = useRef(null);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchCaseDetail();
    }, [caseId]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, asking]);

    const fetchCaseDetail = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/investigations/${caseId}`, { headers });
            setCaseData(res.data);

            // Pre-seed conversation with real case context
            const c = res.data;
            setMessages([
                {
                    role: "user",
                    text: "What triggered this alert?",
                    timestamp: c.createdDate ? new Date(c.createdDate).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" }) : new Date().toLocaleTimeString()
                },
                {
                    role: "assistant",
                    answerTitle: "Alert Trigger Summary",
                    summary: c.oneLineReason || c.narrative?.substring(0, 200) || "Analyzing incident context...",
                    keyIndicators: c.recommendedActions?.slice(0, 3) || [],
                    rawJson: {
                        alertId: c.id,
                        sourceIp: c.targetEntity,
                        event: c.title,
                        severity: c.verdict || "Unknown",
                        confidence: `${c.confidence || 0}%`,
                        status: c.status || "New"
                    },
                    suggestedFollowUps: [
                        "What assets are affected by this incident?",
                        "Show me the related firewall logs",
                        "What remediation steps are recommended?"
                    ],
                    timestamp: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" })
                }
            ]);
        } catch (err) {
            console.error("Failed to load investigation:", err);
        }
        setLoading(false);
    };

    const handleSendMessage = async (queryText) => {
        const text = queryText || inputQuery;
        if (!text.trim() || asking) return;

        setInputQuery("");
        const userMsg = {
            role: "user",
            text,
            timestamp: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" })
        };

        setMessages(prev => [...prev, userMsg]);
        setAsking(true);

        try {
            const res = await axios.post(`${backendURL}/api/investigations/${caseId}/ask`, { question: text }, { headers });
            const botMsg = {
                role: "assistant",
                answerTitle: res.data.answerTitle || "QueryTel Investigation Analysis",
                summary: res.data.summary,
                keyIndicators: res.data.keyIndicators || [],
                rawJson: res.data.rawJson || null,
                suggestedFollowUps: res.data.suggestedFollowUps || [],
                timestamp: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" })
            };
            setMessages(prev => [...prev, botMsg]);
        } catch (err) {
            setMessages(prev => [...prev, {
                role: "assistant",
                answerTitle: "Error",
                summary: "Failed to consult QueryTel Copilot. Please ensure your backend is reachable.",
                keyIndicators: [],
                timestamp: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" })
            }]);
        }
        setAsking(false);
    };

    const handleCopyJson = (jsonObj, idx) => {
        navigator.clipboard.writeText(JSON.stringify(jsonObj, null, 2));
        setCopiedJsonIdx(idx);
        setTimeout(() => setCopiedJsonIdx(null), 1500);
    };

    return (
        <div className="w-full max-w-5xl mx-auto space-y-4 pb-12">
            {/* ─── Top Breadcrumb & Metadata ─── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#111827] border border-white/[0.06] rounded-xl px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400 overflow-x-auto whitespace-nowrap">
                    <Link to="/investigations" className="hover:text-indigo-400 transition-colors">Investigate</Link>
                    <span>/</span>
                    <Link to={`/investigations/${caseId}`} className="hover:text-indigo-400 transition-colors">
                        {caseData?.title || caseId}
                    </Link>
                    <span>/</span>
                    <span className="text-white font-bold flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                        QueryTel Grounded Copilot
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(`/investigations/${caseId}`)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11px] font-semibold transition-colors"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back to Investigation Summary
                    </button>
                </div>
            </div>

            {/* ─── Main Chat Window ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-2xl overflow-hidden shadow-2xl flex flex-col min-h-[640px]">
                
                {/* Chat Header */}
                <div className="p-4 border-b border-white/[0.06] bg-black/40 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-[15px] font-extrabold text-white">Ask QueryTel Anything</h3>
                            <p className="text-[11px] text-slate-400">
                                Grounded in evidence from <strong className="text-slate-200">{caseData?.title || "this investigation"}</strong>.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded bg-rose-500/15 text-rose-400 border border-rose-500/30 text-[10.5px] font-bold uppercase font-mono">
                            Verdict: Malicious (98%)
                        </span>
                    </div>
                </div>

                {/* Chat Feed */}
                <div className="flex-1 p-6 space-y-6 overflow-y-auto max-h-[62vh] custom-scrollbar bg-black/20">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"} space-y-1.5`}>
                            
                            {/* User Bubble */}
                            {msg.role === "user" ? (
                                <div className="max-w-2xl bg-indigo-600 text-white px-4.5 py-3 rounded-2xl rounded-br-sm shadow-md text-[12.5px] leading-relaxed">
                                    <p>{msg.text}</p>
                                    <span className="block text-right text-[10px] text-indigo-200 mt-1 font-mono">{msg.timestamp}</span>
                                </div>
                            ) : (
                                /* AI Response Card */
                                <div className="max-w-3xl w-full bg-[#0F172A] border border-white/[0.08] rounded-2xl rounded-tl-sm p-5 shadow-xl space-y-4">
                                    
                                    {/* Header & Title */}
                                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-2.5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center">
                                                <Bot className="w-3.5 h-3.5" />
                                            </div>
                                            <h4 className="text-[13px] font-bold text-white uppercase tracking-wide">{msg.answerTitle}</h4>
                                        </div>
                                        <span className="text-[10px] text-slate-500 font-mono">{msg.timestamp}</span>
                                    </div>

                                    {/* Summary Paragraph */}
                                    <p className="text-[12.5px] text-slate-200 leading-relaxed font-sans">
                                        {msg.summary}
                                    </p>

                                    {/* Key Indicators Bullets */}
                                    {msg.keyIndicators && msg.keyIndicators.length > 0 && (
                                        <div className="space-y-1.5 bg-black/30 p-3.5 rounded-xl border border-white/[0.04] text-[11.5px]">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                                                Key Forensic Indicators
                                            </span>
                                            {msg.keyIndicators.map((ind, iIdx) => (
                                                <div key={iIdx} className="flex items-start gap-2 text-slate-300">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0 mt-1.5" />
                                                    <span className="leading-snug">{ind}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Raw Event Data JSON Viewer */}
                                    {msg.rawJson && (
                                        <div className="space-y-1.5">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                                                    <Code className="w-3 h-3 text-slate-500" />
                                                    Raw Event Data
                                                </span>
                                                <button
                                                    onClick={() => handleCopyJson(msg.rawJson, idx)}
                                                    className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-white transition-colors"
                                                >
                                                    {copiedJsonIdx === idx ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                                                    {copiedJsonIdx === idx ? "Copied" : "Copy JSON"}
                                                </button>
                                            </div>
                                            <pre className="p-3 bg-black/50 border border-white/[0.06] rounded-xl text-[11px] font-mono text-emerald-400 overflow-x-auto max-h-48 custom-scrollbar leading-relaxed">
                                                {JSON.stringify(msg.rawJson, null, 2)}
                                            </pre>
                                        </div>
                                    )}

                                    {/* Suggested Follow-Up Prompts */}
                                    {msg.suggestedFollowUps && msg.suggestedFollowUps.length > 0 && (
                                        <div className="pt-2 border-t border-white/[0.04] space-y-1.5">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Suggested follow-ups</span>
                                            <div className="flex flex-wrap gap-1.5">
                                                {msg.suggestedFollowUps.map((prompt, pIdx) => (
                                                    <button
                                                        key={pIdx}
                                                        onClick={() => handleSendMessage(prompt)}
                                                        className="text-[11px] bg-white/[0.03] hover:bg-indigo-600/20 hover:border-indigo-500/40 text-slate-300 hover:text-indigo-200 border border-white/[0.08] px-3 py-1 rounded-lg transition-all text-left"
                                                    >
                                                        {prompt} →
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Thinking Indicator */}
                    {asking && (
                        <div className="flex items-start gap-3 bg-[#0F172A] border border-white/[0.08] rounded-2xl p-4 w-72">
                            <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
                            <span className="text-[12px] text-slate-400 font-mono">QueryTel analyzing case telemetry...</span>
                        </div>
                    )}

                    <div ref={chatEndRef} />
                </div>

                {/* Prompt Bar & Suggested Chips */}
                <div className="p-4 border-t border-white/[0.06] bg-black/40 space-y-3">
                    {/* Quick Start Chips */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap mr-1">Quick Prompts:</span>
                        {quickPrompts.map((q, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleSendMessage(q)}
                                className="text-[11px] whitespace-nowrap bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-slate-300 px-3 py-1 rounded-full transition-all"
                            >
                                {q}
                            </button>
                        ))}
                    </div>

                    {/* Query Input */}
                    <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder="Ask why it escalated, whether the file is malicious, or who else was hit..."
                            value={inputQuery}
                            onChange={(e) => setInputQuery(e.target.value)}
                            className="flex-1 bg-black/50 border border-white/[0.08] rounded-xl px-4 py-2.5 text-[12.5px] text-white placeholder:text-slate-500 outline-none focus:border-indigo-500 transition-colors"
                        />
                        <button
                            type="submit"
                            disabled={asking || !inputQuery.trim()}
                            className="flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-xl text-[12px] font-bold shadow-lg shadow-indigo-600/20 disabled:opacity-40 transition-all"
                        >
                            {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Send
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
